import React, { useEffect, useState } from 'react'
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { ScreenClassRender } from 'react-grid-system'
import { useParams } from 'react-router';

import {
  decodeJwt,
  EMessageType,
} from '@unikey/unikey-commons/release/comm';

import {
  useAppDispatch,
  useAppSelector,
  api, 
  addAlert,
  attemptVerifyAccountWithToken,
  SplashContainer,
  PartnerCustomizations, IPartnerCustomizations,
  navConfig, ENavPages,
  checkJWTExpired,
  requestRefreshToken,
  redirectToLogin,
  addPendingRedirect,
  getPendingAdminInvitePath,
} from '../internal';

import { partnerPortalDomain } from '@alias-environment';

export enum EActionTokenTypes {
  Unknown,
  AccountVerification = 'AccountVerification',
  DisableAccount = 'DisableAccount',
  DeleteAccount = 'DeleteAccount',
  PasswordCreate = 'PasswordCreate',
  PasswordReset = 'PasswordReset',
  PasswordChange = 'PasswordChange',
  ChangeEmail = 'EmailChange',
  EkeySharing = 'EKeySharing',
  SiteInvite = 'site_invite',
  SpaceKeyInvite = 'space_key_invite',
  AcceptWallet = 'accept_wallet'
}

interface IProps extends WrappedComponentProps, IPartnerCustomizations {
  history: any,
  match: any,
  accessTokenExists?: boolean,
}

const ActionTokenContainer: React.FC<IProps> = (props) => {

  const dispatch = useAppDispatch();
  const routeParams = useParams();
  
  const $authUser = useAppSelector(state => state.authenticatedUser?.currentUser);
  const $confirmationLoading: boolean = useAppSelector(state => state.actionTokens.loading);
  const $confirmationData = useAppSelector(state => state.actionTokens.data);
  const $accessTokenExists: boolean = !!$authUser;

  useEffect(() => {
    // here also for the refresh case.
    _checkToken();
  }, []);

  const _checkToken = () => {
    const token = routeParams?.actionToken;
    if (token) {
      const decoded: any = decodeJwt(token);
      // if decode returned null, then the token is not a valid jwt
      if (decoded != null) {
        if (!checkJWTExpired(decoded, dispatch, addAlert)) {
          // Depending on the token type, we'll need to handle it differently 
          // and make the appropriate calls depending on the desired token-action
          switch (decoded.pur) {
            case EActionTokenTypes.AccountVerification:
              dispatch(attemptVerifyAccountWithToken({ token })).then(() => {
                setTimeout(() => {
                  // force so the refresh isn't debounced away; the refreshed (now verified) token
                  // runs the post-token routing, which handles any pending admin invite.
                  requestRefreshToken(true).then(() => {
                    if (decoded.sub !== api.getAuthUserId()) {
                      dispatch(addPendingRedirect({ pathname: navConfig.get(ENavPages.logout)!.linkTo() }));
                    } else if (getPendingAdminInvitePath()) {
                      // the post-token routing already sent the user to the pending invite; don't clobber it.
                    } else if (/\/#\/token/.test(window.location.href)) {
                      // if we are still on the token route, then we need to send them to the account landing screen after verifying
                      dispatch(addPendingRedirect({
                        fullUrl: `${partnerPortalDomain}/#${navConfig.get(ENavPages.myAccount)!.linkTo([])}`,
                        useHistoryReplace: false
                      }));
                    }
                  }).catch(() => {
                    // likely a login_required error on the refresh token request
                    dispatch(addPendingRedirect({ pathname: navConfig.get(ENavPages.logout)!.linkTo(), delay: 1000 }));
                  })
                }, 3000);

              });
              break;
            case EActionTokenTypes.PasswordCreate:
            case EActionTokenTypes.PasswordChange:
            case EActionTokenTypes.PasswordReset:
              dispatch(addPendingRedirect({ pathname: navConfig.get(ENavPages.setPassword)!.linkTo([token]) }));
              break; 
            case EActionTokenTypes.AcceptWallet:
                dispatch(addPendingRedirect({
                  pathname: navConfig.get(ENavPages.acceptWallet)!.linkTo([token]),
                  priority: 'high'
                }));

               break;
                           
            default:
              dispatch(addAlert({
                id: Date.now(),
                type: EMessageType.warn,
                titleKey: 'unsupportedAction',
                messageKeys: [decoded.pur]
              }))
              break;
          }
        }
      } 
    } else {
      // we are redirecting with the entire domain since we may be on the /jwtaction/#/token route here.
      // /jwtaction allows the proxy to ignore the user opt-in preferences so that email links always work
      // even if the user has not opted in to this portal. After accepting this token, we want to 
      // replace with non /jwtaction route to allow the user opt-in preferences to take effect again
      dispatch(addPendingRedirect({
        fullUrl: `${partnerPortalDomain}/#/logout`,
        useHistoryReplace: true
      }));
    }
  }

  return (
    <ScreenClassRender render={(screenClass: string) => (
      <section className="access-container">
         <SplashContainer
          fromComponentName="actiontoken"
          match={props.match}
          history={props.history}
          externalRedirect={true} />
      </section>
    )} />
  )
}

export default PartnerCustomizations(injectIntl(ActionTokenContainer), {
  componentName: 'ActionToken'
})